import { useLang } from 'hooks/lang';

import { Button, IconButton, ResponsiveOverlay, RadioInput } from 'components/Blocks';

import { TextSearchIcon } from 'components/Icons';

import { ConfigDateRange } from './DateRange';
import { TeamSelect } from './TeamSelect';

import { useUser } from 'hooks/user';
import { useFormControl } from 'hooks';

import { LoadConfigState, LoadReference } from './types';
import { toTruthyList } from 'utils/array';
import { cn } from 'styles';

interface LoadConfigProps {
  children?: never;
  state: LoadConfigState;
  onSave: (config: LoadConfigState) => void;
}

export function LoadConfig({ state: loadState, onSave }: LoadConfigProps): JSX.Element {
  const user = useUser();

  const { t } = useLang();

  const { dispatch, state } = useFormControl<LoadConfigState>(loadState, {
    watch: true,
  });

  const handleRefChange = (ref: LoadReference): void => {
    dispatch({ ref, team: ref === 'TEAM' ? '' : null });
  };

  return (
    <ResponsiveOverlay
      default="pop-over"
      title="Load Configuration"
      largeClassName="w-auto"
      className="text-start"
      align="end"
      enforceDrawerMinHeight={false}
      trigger={
        <IconButton>
          <TextSearchIcon />
        </IconButton>
      }
    >
      <RadioInput
        orientation="horizontal"
        onChangeDirect={handleRefChange as () => void}
        value={state.ref}
        className={cn(
          'min-w-[105px] flex-shrink-0 whitespace-nowrap border px-3 py-1 text-sm font-medium first-of-type:rounded-l-full last-of-type:rounded-r-full hover:bg-hover small_phone:min-w-fit',
          user.team.canAssumeProject ? 'w-1/3' : 'w-1/2',
        )}
        selectedClassName="border-primary text-primary hover:bg-transparent"
        addDot={false}
        gap="0"
        options={toTruthyList([
          { value: 'STATUS', label: t('status') },
          { value: 'TEAM', label: t('team') },
          user.team.canAssumeProject ? { value: 'OWN', label: t('my-projects') } : null,
        ])}
      />

      {state.ref === 'TEAM' && (
        <TeamSelect team={state.team as string} setTeam={(team) => dispatch({ team })} />
      )}

      <ConfigDateRange state={state} dispatch={dispatch} />

      <Button disabled={state.ref === 'TEAM' ? !state.team : false} onClick={() => onSave(state)}>
        {t('save')}
      </Button>
    </ResponsiveOverlay>
  );
}
